import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button } from "@/components/ui/button";
import {
  PlusCircle,
  Pencil,
  Trash2,
  Search,
  X,
  Star,
  Clock,
  Users,
  BookOpen,

  Image as ImageIcon
} from "lucide-react";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import LayoutSwitcher, { LayoutType } from "@/components/layout/Training/LayoutSwitcher";
import { cn } from "@/lib/utils";
import AddTrainingModal from "@/components/modals/TrainingMultiStepAdd/AddTrainingModal";
import { RootState } from "@/store";
import {
  fetchFilteredTrainings,
  setTrainingFilter,
  resetTrainingFilter,
  deleteTraining // Add this import
} from "@/features/training/trainingSlice";
import { Training, TrainingFilter } from "@/features/training/types";

import {  useQuery } from "@apollo/client";
import { GET_TRAINING_IMAGE } from "@/lib/graphql/queries";
import { toast } from "@/components/ui/use-toast"; // Import toast for notifications

// Define the GraphQL query for fetching training images

import { useNavigate } from "react-router-dom"; // Import useNavigate
// Image component that fetches and displays training images
const TrainingImage = ({ filename, alt, className }: { filename: string, alt: string, className: string }) => {
  const { loading, error, data } = useQuery(GET_TRAINING_IMAGE, {
    variables: { filename },
    skip: !filename || filename.startsWith('http')
  });

  if (loading) return (
    <div className={`${className} flex items-center justify-center bg-gray-100`}>
      <ImageIcon className="text-gray-400" />
    </div>
  );

  if (error || !data) {
    console.error("Error loading image:", error);
    return (
      <div className={`${className} flex items-center justify-center bg-gray-100`}>
        <ImageIcon className="text-gray-400" />
      </div>
    );
  }

  // If the image is already a URL, use it directly
  if (filename.startsWith('http')) {
    return <img src={filename} alt={alt} className={className} />;
  }

  // Otherwise use the base64 data from the query
  if (data.getTrainingImageByName?.base64Data) {
    const { base64Data, mimetype } = data.getTrainingImageByName;
    return <img src={`data:${mimetype};base64,${base64Data}`} alt={alt} className={className} />;
  }

  // Fallback
  return (
    <div className={`${className} flex items-center justify-center bg-gray-100`}>
      <ImageIcon className="text-gray-400" />
    </div>
  );
};



const TrainingsManagement = () => {
  const navigate = useNavigate(); // Initialize useNavigate
  const dispatch = useDispatch();
  const {
    trainingsList,
    paginationMeta,
    trainingsLoading,
    trainingsError,
    filter,
    deleteLoading // Get delete loading state from Redux
  } = useSelector((state: RootState) => state.training);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedTraining, setSelectedTraining] = useState<Training | null>(null);

  // State for filters
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string>("");
  const [selectedLevel, setSelectedLevel] = useState<string>("");
  const [selectedFormat, setSelectedFormat] = useState<string>("");
  const [certificate, setCertificate] = useState<string>("");
  const [layout, setLayout] = useState<LayoutType>("grid");

  const containerClasses = {
    grid: "grid md:grid-cols-2 lg:grid-cols-3 gap-6",
    list: "space-y-6",
    gallery: "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-8",
  };

  // Fetch trainings on component mount
 // Fetch trainings when filter changes
 useEffect(() => {
  dispatch(fetchFilteredTrainings(filter) as any);
}, [dispatch, filter]);

  const handleDelete = (training: Training) => {
    setSelectedTraining(training);
    setIsDeleteDialogOpen(true);
  };

  // Add the confirmDelete function to use Redux
  const confirmDelete = () => {
    if (selectedTraining) {
      // Dispatch the delete action from Redux
      dispatch(deleteTraining(parseInt(selectedTraining.id.toString(), 10)) as any)
        .unwrap()
        .then(() => {
          toast({
            title: "Training Deleted",
            description: "The training has been successfully deleted.",
          });
          setIsDeleteDialogOpen(false);
          setSelectedTraining(null);
        })
        .catch((error: any) => {
          toast({
            title: "Error",
            description: `Failed to delete training: ${error}`,
            variant: "destructive",
          });
        });
    }
  };

  // Handle search
  const handleSearch = () => {
    dispatch(setTrainingFilter({ search: searchTerm, page: 1 }) as any);
    dispatch(fetchFilteredTrainings({ ...filter, search: searchTerm, page: 1 }) as any);
  };

  // Handle category filter
  const handleCategoryFilter = (category: string) => {
    setSelectedCategory(category);
    const categoryValue = category === "ALL" ? null : category.toLowerCase();
    dispatch(setTrainingFilter({ category: categoryValue, page: 1 }) as any);
    dispatch(fetchFilteredTrainings({ ...filter, category: categoryValue, page: 1 }) as any);
  };

  // Handle level filter
  const handleLevelFilter = (level: string) => {
    setSelectedLevel(level);
    const levelValue = level === "ALL" ? null : level.toLowerCase();
    dispatch(setTrainingFilter({ level: levelValue, page: 1 }) as any);
    dispatch(fetchFilteredTrainings({ ...filter, level: levelValue, page: 1 }) as any);
  };

  // Handle format filter
  const handleFormatFilter = (format: string) => {
    setSelectedFormat(format);
    const formatValue = format === "ALL" ? null : format.toLowerCase();
    dispatch(setTrainingFilter({ format: formatValue, page: 1 }) as any);
    dispatch(fetchFilteredTrainings({ ...filter, format: formatValue, page: 1 }) as any);
  };

  // Handle pagination
  const handlePageChange = (page: number) => {
    dispatch(setTrainingFilter({ page }) as any);
    dispatch(fetchFilteredTrainings({ ...filter, page }) as any);
  };

  // Update certificate filter handler to use select
  const handleCertificateFilter = (value: string) => {
    setCertificate(value);
    let certificateValue = null;
    if (value === "yes") {
      certificateValue = true;
    } else if (value === "no") {
      certificateValue = false;
    }
    dispatch(setTrainingFilter({ certificate: certificateValue, page: 1 }) as any);
    dispatch(fetchFilteredTrainings({ ...filter, certificate: certificateValue, page: 1 }) as any);
  };

  // Update reset filters to include certificate
  const resetFilters = () => {
    setSearchTerm("");
    setSelectedCategory("");
    setSelectedLevel("");
    setSelectedFormat("");
    setCertificate("all"); // Changed from empty string to "all"
    dispatch(resetTrainingFilter() as any);
    dispatch(fetchFilteredTrainings({
      search: null,
      category: null,
      level: null,
      format: null,
      certificate: null,
      location: null,
      minPrice: null,
      maxPrice: null,
      page: 1,
      limit: 10,
    } as TrainingFilter) as any);
  };

  // Handle search function is now directly used in onKeyDown

  const handleTrainingClick = (trainingId: number) => {
    navigate(`/admin/trainings/${trainingId}`);
  };


  const renderTraining = (training: Training) => {
    switch (layout) {
      case "grid":
        return (
          <Card
            key={training.id}
            className="flex flex-col hover:shadow-lg transition-all duration-300 cursor-pointer"
            onClick={() => handleTrainingClick(training.id)} // Add onClick handler
          >
            <div className="relative">
              <TrainingImage
                filename={training.image}
                alt={training.title}
                className="w-full h-48 object-cover rounded-t-lg"
              />
              <Badge
                variant={training.format.toLowerCase() === "online" ? "default" : "secondary"}
                className="absolute top-2 right-2"
              >
                {training.format}
              </Badge>
            </div>
            <CardHeader className="flex-grow">
              <CardTitle>{training.title}</CardTitle>
              <div className="flex items-center justify-between text-sm text-muted-foreground">
                <span>{training.provider}</span>
                <div className="flex items-center gap-1">
                  <Star size={14} className="text-yellow-500" />
                  {training.rating || 4.5}/5
                </div>
              </div>
            </CardHeader>
            <CardContent className="flex-grow">
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="flex items-center gap-2">
                  <Clock size={14} />
                  <span>{training.duration}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Users size={14} />
                  <span>{training.enrollments || 0} Enrolled</span>
                </div>
                <div className="flex items-center gap-2">
                  <BookOpen size={14} />
                  <span>{training.level}</span>
                </div>
              </div>
            </CardContent>
            <CardFooter className="flex justify-between">
              <Button
                variant="outline"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation(); // Prevent card click
                  console.log(`Edit ${training.title}`);
                }}
              >
                <Pencil size={14} className="mr-2" />
                Edit
              </Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation(); // Prevent card click
                  handleDelete(training);
                }}
              >
                <Trash2 size={14} className="mr-2" />
                Delete
              </Button>
            </CardFooter>
          </Card>
        );

      case "list":
        return (
          <Card key={training.id} className="overflow-hidden hover:shadow-lg transition-all duration-300">
            <div className="flex flex-col md:flex-row">
              <div className="md:w-1/3 relative">
                <TrainingImage
                  filename={training.image}
                  alt={training.title}
                  className="h-48 md:h-full w-full object-cover"
                />
                <Badge
                  variant={training.format.toLowerCase() === "online" ? "default" : "secondary"}
                  className="absolute top-2 right-2"
                >
                  {training.format}
                </Badge>
              </div>
              <div className="flex-1 p-6">
                <CardTitle className="mb-2">{training.title}</CardTitle>
                <div className="flex items-center justify-between mb-4">
                  <span className="text-sm text-muted-foreground">{training.provider}</span>
                  <div className="flex items-center gap-1">
                    <Star size={14} className="text-yellow-500" />
                    <span>{training.rating || 4.5}/5</span>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground mb-4">{training.description}</p>
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="flex items-center gap-2">
                    <Clock size={14} />
                    <span>{training.duration}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Users size={14} />
                    <span>{training.enrollments || 0} Enrolled</span>
                  </div>
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" size="sm" onClick={() => console.log(`Edit ${training.title}`)}>
                    <Pencil size={14} className="mr-2" />
                    Edit
                  </Button>
                  <Button variant="destructive" size="sm" onClick={() => handleDelete(training)}>
                    <Trash2 size={14} className="mr-2" />
                    Delete
                  </Button>
                </div>
              </div>
            </div>
          </Card>
        );

      case "gallery":
        return (
          <Card key={training.id} className="group cursor-pointer hover:shadow-lg transition-shadow">
            <div className="relative aspect-video overflow-hidden rounded-t-lg">
              <TrainingImage
                filename={training.image}
                alt={training.title}
                className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                <h3 className="text-xl font-semibold mb-2">{training.title}</h3>
                <span className="text-sm text-white/90">{training.provider}</span>
              </div>
              <Badge
                variant={training.format.toLowerCase() === "online" ? "default" : "secondary"}
                className="absolute top-2 right-2"
              >
                {training.format}
              </Badge>
            </div>
            <CardContent className="p-4">
              <div className="flex justify-between items-center mb-4">
                <div className="flex items-center gap-1">
                  <Star size={14} className="text-yellow-500" />
                  <span>{training.rating || 4.5}/5</span>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => console.log(`Edit ${training.title}`)}>
                    <Pencil size={14} />
                  </Button>
                  <Button variant="destructive" size="sm" onClick={() => handleDelete(training)}>
                    <Trash2 size={14} />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        );
    }
  };

  return (
    <div className="container mx-auto px-4 py-6">
      {/* Enhanced Header Section */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8 pb-6 border-b">
        <div>
          <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-2">
            <BookOpen className="h-8 w-8 text-primary" />
            Trainings Management
          </h1>
          <p className="text-muted-foreground mt-1">Create, manage, and track your training courses</p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            onClick={() => resetFilters()}
            className="flex items-center gap-2"
            size="sm"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="animate-spin-once">
              <path d="M21 12a9 9 0 0 0-9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
              <path d="M3 3v5h5" />
              <path d="M3 12a9 9 0 0 0 9 9 9.75 9.75 0 0 0 6.74-2.74L21 16" />
              <path d="M16 21h5v-5" />
            </svg>
            Reset
          </Button>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  onClick={() => setIsModalOpen(true)}
                  className="flex items-center gap-2"
                  size="default"
                >
                  <PlusCircle size={16} />
                  New Training
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Add a new training course</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-blue-600">Total Trainings</p>
              <h3 className="text-2xl font-bold">{paginationMeta?.totalItems || 0}</h3>
            </div>
            <div className="h-12 w-12 bg-blue-100 rounded-full flex items-center justify-center">
              <BookOpen className="h-6 w-6 text-blue-600" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-green-600">Active Enrollments</p>
              <h3 className="text-2xl font-bold">{trainingsList.reduce((acc, training) => acc + (training.enrollments || 0), 0)}</h3>
            </div>
            <div className="h-12 w-12 bg-green-100 rounded-full flex items-center justify-center">
              <Users className="h-6 w-6 text-green-600" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-purple-600">Online Courses</p>
              <h3 className="text-2xl font-bold">{trainingsList.filter(t => t.format.toLowerCase() === 'online').length}</h3>
            </div>
            <div className="h-12 w-12 bg-purple-100 rounded-full flex items-center justify-center">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-6 w-6 text-purple-600">
                <rect width="18" height="12" x="3" y="6" rx="2" />
                <path d="m5 15 7-3 7 3" />
              </svg>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-amber-600">Avg. Rating</p>
              <h3 className="text-2xl font-bold flex items-center gap-1">
                {trainingsList.length > 0
                  ? (trainingsList.reduce((acc, training) => acc + (training.rating || 4.5), 0) / trainingsList.length).toFixed(1)
                  : "0.0"}
                <Star size={16} className="text-amber-500 mt-1" />
              </h3>
            </div>
            <div className="h-12 w-12 bg-amber-100 rounded-full flex items-center justify-center">
              <Star className="h-6 w-6 text-amber-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      <AddTrainingModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />

      {/* Enhanced Filter Section */}
      <Card className="mb-8 border border-muted/60 shadow-sm overflow-hidden transition-all duration-300 hover:shadow-md">
        <CardHeader className="bg-muted/20 pb-3 pt-4 border-b">
          <CardTitle className="text-lg flex items-center gap-2">
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
            </svg>
            Search & Filters
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-4">
          {/* Search Bar */}
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
              <Input
                placeholder="Search by title, provider, or description..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                className="pl-10 w-full transition-all duration-300 border-muted focus:border-primary"
              />
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={handleSearch}
                variant="default"
                className="flex items-center gap-2 min-w-[100px] transition-all duration-300"
              >
                <Search size={16} />
                Search
              </Button>
              <LayoutSwitcher currentLayout={layout} onLayoutChange={setLayout} />
            </div>
          </div>

          {/* Filter Controls */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            {/* Category Filter */}
            <div className="space-y-2">
              <label className="text-sm font-medium flex items-center gap-1">
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 20h16a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.93a2 2 0 0 1-1.66-.9l-.82-1.2A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13c0 1.1.9 2 2 2Z" />
                </svg>
                Category
              </label>
              <Select value={selectedCategory || "ALL"} onValueChange={handleCategoryFilter}>
                <SelectTrigger className="bg-white transition-all duration-300 hover:border-primary">
                  <SelectValue placeholder="All Categories" />
                </SelectTrigger>
                <SelectContent className="bg-white z-50">
                  <SelectItem value="ALL">All Categories</SelectItem>
                  <SelectItem value="programming">Programming</SelectItem>
                  <SelectItem value="design">Design</SelectItem>
                  <SelectItem value="business">Business</SelectItem>
                  <SelectItem value="marketing">Marketing</SelectItem>
                  <SelectItem value="data science">Data Science</SelectItem>
                  <SelectItem value="personal development">Personal Development</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Level Filter */}
            <div className="space-y-2">
              <label className="text-sm font-medium flex items-center gap-1">
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M2 20h.01" />
                  <path d="M7 20v-4" />
                  <path d="M12 20v-8" />
                  <path d="M17 20v-12" />
                  <path d="M22 20V8" />
                </svg>
                Level
              </label>
              <Select value={selectedLevel || "ALL"} onValueChange={handleLevelFilter}>
                <SelectTrigger className="bg-white transition-all duration-300 hover:border-primary">
                  <SelectValue placeholder="All Levels" />
                </SelectTrigger>
                <SelectContent className="bg-white z-50">
                  <SelectItem value="ALL">All Levels</SelectItem>
                  <SelectItem value="beginner">Beginner</SelectItem>
                  <SelectItem value="intermediate">Intermediate</SelectItem>
                  <SelectItem value="advanced">Advanced</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Format Filter */}
            <div className="space-y-2">
              <label className="text-sm font-medium flex items-center gap-1">
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect width="18" height="12" x="3" y="6" rx="2" />
                  <path d="m5 15 7-3 7 3" />
                </svg>
                Format
              </label>
              <Select value={selectedFormat || "ALL"} onValueChange={handleFormatFilter}>
                <SelectTrigger className="bg-white transition-all duration-300 hover:border-primary">
                  <SelectValue placeholder="All Formats" />
                </SelectTrigger>
                <SelectContent className="bg-white z-50">
                  <SelectItem value="ALL">All Formats</SelectItem>
                  <SelectItem value="online">Online</SelectItem>
                  <SelectItem value="in-person">In-Person</SelectItem>
                  <SelectItem value="hybrid">Hybrid</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Certificate Filter */}
            <div className="space-y-2">
              <label className="text-sm font-medium flex items-center gap-1">
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect width="18" height="14" x="3" y="5" rx="2" />
                  <path d="M21 8H8" />
                  <path d="M21 12H8" />
                  <path d="M21 16H8" />
                  <path d="M4 8h.01" />
                  <path d="M4 12h.01" />
                  <path d="M4 16h.01" />
                </svg>
                Certificate
              </label>
              <Select value={certificate || "all"} onValueChange={handleCertificateFilter}>
                <SelectTrigger className="bg-white transition-all duration-300 hover:border-primary">
                  <SelectValue placeholder="All Certificates" />
                </SelectTrigger>
                <SelectContent className="bg-white z-50">
                  <SelectItem value="all">All Certificates</SelectItem>
                  <SelectItem value="yes">With Certificate</SelectItem>
                  <SelectItem value="no">No Certificate</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Active Filters */}
          {(selectedCategory !== "ALL" || selectedLevel !== "ALL" || selectedFormat !== "ALL" || certificate !== "all") && (
            <div className="mt-4 pt-4 border-t border-muted/60">
              <div className="flex flex-wrap gap-2 items-center">
                <span className="text-sm font-medium text-muted-foreground">Active filters:</span>

                {selectedCategory !== "ALL" && (
                  <Badge
                    variant="outline"
                    className="flex items-center gap-1 bg-primary/5 hover:bg-primary/10 transition-colors duration-300"
                  >
                    Category: {selectedCategory}
                    <X
                      className="h-3 w-3 ml-1 cursor-pointer"
                      onClick={() => handleCategoryFilter("ALL")}
                    />
                  </Badge>
                )}

                {selectedLevel !== "ALL" && (
                  <Badge
                    variant="outline"
                    className="flex items-center gap-1 bg-primary/5 hover:bg-primary/10 transition-colors duration-300"
                  >
                    Level: {selectedLevel}
                    <X
                      className="h-3 w-3 ml-1 cursor-pointer"
                      onClick={() => handleLevelFilter("ALL")}
                    />
                  </Badge>
                )}

                {selectedFormat !== "ALL" && (
                  <Badge
                    variant="outline"
                    className="flex items-center gap-1 bg-primary/5 hover:bg-primary/10 transition-colors duration-300"
                  >
                    Format: {selectedFormat}
                    <X
                      className="h-3 w-3 ml-1 cursor-pointer"
                      onClick={() => handleFormatFilter("ALL")}
                    />
                  </Badge>
                )}

                {certificate !== "all" && (
                  <Badge
                    variant="outline"
                    className="flex items-center gap-1 bg-primary/5 hover:bg-primary/10 transition-colors duration-300"
                  >
                    Certificate: {certificate === "yes" ? "Yes" : "No"}
                    <X
                      className="h-3 w-3 ml-1 cursor-pointer"
                      onClick={() => handleCertificateFilter("all")}
                    />
                  </Badge>
                )}

                <Button
                  variant="ghost"
                  size="sm"
                  onClick={resetFilters}
                  className="h-7 px-2 text-xs flex items-center gap-1 transition-colors duration-300"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 3h18v18H3z" />
                    <path d="m15 9-6 6" />
                    <path d="m9 9 6 6" />
                  </svg>
                  Clear all
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Loading State */}
      {trainingsLoading && (
        <Card className="border border-muted/40 shadow-sm overflow-hidden">
          <div className="flex flex-col justify-center items-center py-16">
            <div className="relative w-16 h-16 mb-4">
              <svg className="animate-spin w-16 h-16 text-primary/30" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <BookOpen className="w-6 h-6 text-primary" />
              </div>
            </div>
            <h3 className="text-lg font-medium mb-1">Loading trainings</h3>
            <p className="text-muted-foreground">Please wait while we fetch your trainings...</p>
          </div>
        </Card>
      )}

      {/* Error State */}
      {trainingsError && (
        <Card className="border-destructive/30 bg-destructive/5 shadow-sm overflow-hidden mb-6">
          <CardContent className="p-6 flex items-start gap-4">
            <div className="rounded-full bg-destructive/10 p-2 flex-shrink-0">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-destructive">
                <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" />
                <path d="M12 9v4" />
                <path d="M12 17h.01" />
              </svg>
            </div>
            <div>
              <h3 className="text-lg font-medium mb-1 text-destructive">Error loading trainings</h3>
              <p className="text-muted-foreground mb-4">{trainingsError}</p>
              <Button variant="outline" size="sm" onClick={() => window.location.reload()} className="flex items-center gap-1.5">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 12a9 9 0 0 0-9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
                  <path d="M3 3v5h5" />
                  <path d="M3 12a9 9 0 0 0 9 9 9.75 9.75 0 0 0 6.74-2.74L21 16" />
                  <path d="M16 21h5v-5" />
                </svg>
                Try Again
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Empty State */}
      {!trainingsLoading && trainingsList.length === 0 && (
        <Card className="border border-muted/40 shadow-sm overflow-hidden">
          <div className="flex flex-col justify-center items-center py-16 px-4">
            <div className="w-20 h-20 rounded-full bg-muted/30 flex items-center justify-center mb-6">
              <BookOpen className="w-10 h-10 text-muted-foreground/60" />
            </div>
            <h3 className="text-xl font-medium mb-2">No trainings found</h3>
            <p className="text-muted-foreground mb-6 text-center max-w-md">
              We couldn't find any trainings matching your current filters. Try adjusting your search criteria or add a new training.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button variant="outline" onClick={resetFilters} className="flex items-center gap-1.5">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 12a9 9 0 0 0-9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
                  <path d="M3 3v5h5" />
                  <path d="M3 12a9 9 0 0 0 9 9 9.75 9.75 0 0 0 6.74-2.74L21 16" />
                  <path d="M16 21h5v-5" />
                </svg>
                Reset Filters
              </Button>
              <Button onClick={() => setIsModalOpen(true)} className="flex items-center gap-1.5">
                <PlusCircle size={16} />
                Add Training
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Trainings Grid/List/Gallery */}
      {!trainingsLoading && trainingsList.length > 0 && (
        <div className={cn("transition-all duration-300", containerClasses[layout])}>
          {trainingsList.map(renderTraining)}
        </div>
      )}


      {/* Pagination */}
      {paginationMeta && (
        <div className="mt-6">
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  onClick={() => handlePageChange(Math.max(1, paginationMeta.currentPage - 1))}
                  className={paginationMeta.currentPage <= 1 ? "pointer-events-none opacity-50" : "cursor-pointer"}
                />
              </PaginationItem>

              {/* Limit the number of page buttons shown for better UX */}
              {(() => {
                // Calculate which page numbers to show
                const totalPages = paginationMeta.totalPages || 1;
                const currentPage = paginationMeta.currentPage || 1;

                // Show max 5 pages
                let startPage = Math.max(1, currentPage - 2);
                let endPage = Math.min(totalPages, startPage + 4);

                // Adjust if we're near the end
                if (endPage - startPage < 4) {
                  startPage = Math.max(1, endPage - 4);
                }

                return Array.from(
                  { length: endPage - startPage + 1 },
                  (_, i) => startPage + i
                ).map(page => (
                  <PaginationItem key={page}>
                    <PaginationLink
                      isActive={page === currentPage}
                      onClick={() => handlePageChange(page)}
                      className="cursor-pointer"
                    >
                      {page}
                    </PaginationLink>
                  </PaginationItem>
                ));
              })()}

              <PaginationItem>
                <PaginationNext
                  onClick={() =>
                    paginationMeta.currentPage < (paginationMeta.totalPages || 1) &&
                    handlePageChange(paginationMeta.currentPage + 1)
                  }
                  className={
                    paginationMeta.currentPage >= (paginationMeta.totalPages || 1)
                      ? "pointer-events-none opacity-50"
                      : "cursor-pointer"
                  }
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>

          {/* Add items per page selector */}
          <div className="flex justify-end mt-4">
            <Select
              value={String(filter.limit || 10)}
              onValueChange={(value) => {
                const limit = parseInt(value);
                dispatch(setTrainingFilter({ limit, page: 1 }) as any);
                dispatch(fetchFilteredTrainings({ ...filter, limit, page: 1 }) as any);
              }}
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Items per page" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="5">5 per page</SelectItem>
                <SelectItem value="10">10 per page</SelectItem>
                <SelectItem value="20">20 per page</SelectItem>
                <SelectItem value="50">50 per page</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      )}


      {/* Delete Confirmation Dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Training</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{selectedTraining?.title}"?
              This action cannot be undone and will permanently remove the training course.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteLoading}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              disabled={deleteLoading}
              className={deleteLoading ? "opacity-50 cursor-not-allowed" : ""}
            >
              {deleteLoading ? "Deleting..." : "Delete Training"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default TrainingsManagement;